import React, { FC, ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { RouteGuardType } from '../model/type/route-guard.type';
import { OvPermissionType } from '../model/type/ov-permission.type';
import { useAppSelector } from '../../redux/hooks';
import { isUndefined } from 'lodash';
import { Constants } from '../constants';

interface AuthRouteProps {
  type: RouteGuardType;
  redirectPath?: string;
  permissions: OvPermissionType[];
  children: ReactNode;
}

const AuthRoute: FC<AuthRouteProps> = ({ type, redirectPath, children }) => {
  const isAuthenticated = useAppSelector((state) => state.auth.authenticated);
  const location = useLocation();

  if (!isUndefined(isAuthenticated)) {
    // call routing logic only after the authentication data has been set
    if (redirectPath) {
      // logged in
      if (type === 'public' && isAuthenticated) {
        const preservedLocation = sessionStorage.getItem(
          Constants.SESSION_STORAGE_KEYS.LOCATION_BEFORE_AUTHENTICATION
        );

        // in case there is a preserved location in session storage, redirect there
        if (
          preservedLocation?.startsWith('/') &&
          !preservedLocation.startsWith('//')
        ) {
          sessionStorage.removeItem(
            Constants.SESSION_STORAGE_KEYS.LOCATION_BEFORE_AUTHENTICATION
          );
          return <Navigate to={preservedLocation} replace />;
        }

        sessionStorage.removeItem(
          Constants.SESSION_STORAGE_KEYS.LOCATION_BEFORE_AUTHENTICATION
        );

        // otherwise redirect to the default redirect path (user list or patients list)
        return <Navigate to={redirectPath} replace />;
      } else if (type === 'protected' && !isAuthenticated) {
        // in case you hit a protected route, but you are not authenticated, preserve the original destination for later reuse
        const locationFrom = `${location.pathname}${location.search}${location.hash}`;

        if (locationFrom.startsWith('/') && !locationFrom.startsWith('//')) {
          sessionStorage.setItem(
            Constants.SESSION_STORAGE_KEYS.LOCATION_BEFORE_AUTHENTICATION,
            locationFrom
          );
        }

        return (
          <Navigate to={redirectPath} state={{ from: locationFrom }} replace />
        );
      }
    }
  }

  return <>{children}</>;
};

export default AuthRoute;
